import React, { useEffect, useRef, useState } from 'react';
import {
  Box, Container, Typography, Button, Grid, Card, CardContent,
  Chip, Paper, Stack, IconButton, Fade, Slide, Grow,
  useTheme, Avatar, Divider
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  ArrowForward as ArrowForwardIcon,
  TrendingUp as TrendingUpIcon,
  Security as SecurityIcon,
  Speed as SpeedIcon,
  People as PeopleIcon,
  MedicalServices as MedicalServicesIcon,
  Science as ScienceIcon,
  Vaccines as VaccinesIcon,
  MonitorHeart as MonitorHeartIcon,
  LocalHospital as LocalHospitalIcon,
  Psychology as PsychologyIcon,
  Biotech as BiotechIcon,
  PlayArrow as PlayArrowIcon,
  ChevronRight as ChevronRightIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';

const Home = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const services = [
    { 
      icon: <ScienceIcon sx={{ fontSize: 40 }} />, 
      title: 'Hepatology Center', 
      description: 'Advanced liver care with cutting-edge technology and specialized expertise.',
      gradient: 'linear-gradient(135deg, #00d4ff 0%, #0066ff 100%)',
      badge: 'Excellence'
    },
    { 
      icon: <BiotechIcon sx={{ fontSize: 40 }} />, 
      title: 'Infectious Disease', 
      description: 'Comprehensive management of HIV, TB, malaria with proven protocols.',
      gradient: 'linear-gradient(135deg, #ff6b6b 0%, #ff3355 100%)',
      badge: 'Specialized'
    },
    { 
      icon: <MonitorHeartIcon sx={{ fontSize: 40 }} />, 
      title: 'Internal Medicine', 
      description: 'Holistic chronic disease management with personalized care plans.',
      gradient: 'linear-gradient(135deg, #ffaa00 0%, #ff6b00 100%)',
      badge: 'Comprehensive'
    },
    { 
      icon: <MedicalServicesIcon sx={{ fontSize: 40 }} />, 
      title: 'Diagnostic Imaging', 
      description: 'State-of-the-art imaging including CT, MRI, Ultrasound, and X-Ray.',
      gradient: 'linear-gradient(135deg, #00ff88 0%, #00cc66 100%)',
      badge: 'Advanced'
    },
    { 
      icon: <VaccinesIcon sx={{ fontSize: 40 }} />, 
      title: 'Medical Laboratory', 
      description: 'Full-spectrum diagnostic testing with rapid results and accuracy.',
      gradient: 'linear-gradient(135deg, #a855f7 0%, #7c3aed 100%)',
      badge: 'Accredited'
    },
    { 
      icon: <LocalHospitalIcon sx={{ fontSize: 40 }} />, 
      title: 'Pharmacy Services', 
      description: 'Integrated pharmacy with prescription services and medication counseling.',
      gradient: 'linear-gradient(135deg, #f472b6 0%, #ec4899 100%)',
      badge: 'Integrated'
    },
  ];

  const stats = [
    { value: '10+', label: 'Specialties', icon: <MedicalServicesIcon /> },
    { value: '50+', label: 'Specialists', icon: <PeopleIcon /> },
    { value: '97%', label: 'Satisfaction', icon: <TrendingUpIcon /> },
    { value: '24/7', label: 'Emergency', icon: <SecurityIcon /> },
  ];

  const features = [
    { icon: <SecurityIcon />, title: 'Secure & Private', desc: 'DSSN-protected health records' },
    { icon: <SpeedIcon />, title: 'Fast & Efficient', desc: 'Digital appointments & payments' },
    { icon: <TrendingUpIcon />, title: 'Quality Care', desc: 'Evidence-based medicine' },
    { icon: <PeopleIcon />, title: 'Patient-Centered', desc: 'Your health, our priority' },
  ];

  return (
    <Box>
      {/* Hero Section - Badass Dark Theme */}
      <Box sx={{
        minHeight: '100vh',
        background: 'linear-gradient(180deg, #0a0a0a 0%, #141414 50%, #1a1a2e 100%)',
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
      }}>
        {/* Animated Background */}
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(circle at 20% 50%, rgba(0, 212, 255, 0.15) 0%, transparent 50%),
            radial-gradient(circle at 80% 80%, rgba(255, 107, 107, 0.1) 0%, transparent 50%),
            radial-gradient(circle at 40% 20%, rgba(0, 255, 136, 0.05) 0%, transparent 50%)
          `,
          zIndex: 0,
        }} />
        
        {/* Floating Particles */}
        <Box sx={{ position: 'absolute', width: '100%', height: '100%', zIndex: 0 }}>
          {[...Array(20)].map((_, i) => (
            <Box
              key={i}
              sx={{
                position: 'absolute',
                width: 4,
                height: 4,
                background: `rgba(0, 212, 255, ${0.1 + Math.random() * 0.3})`,
                borderRadius: '50%',
                top: `${Math.random() * 100}%`,
                left: `${Math.random() * 100}%`,
                animation: `float ${10 + Math.random() * 20}s infinite`,
                animationDelay: `${Math.random() * 5}s`,
              }}
            />
          ))}
        </Box>

        <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} lg={7}>
              <Fade in={isVisible} timeout={1000}>
                <Box>
                  <Chip 
                    label="🚀 Digital Liberia Ecosystem" 
                    sx={{ 
                      mb: 3, 
                      bgcolor: 'rgba(0, 212, 255, 0.15)',
                      color: '#00d4ff',
                      border: '1px solid rgba(0, 212, 255, 0.2)',
                      fontWeight: 600,
                      fontSize: '0.9rem',
                      py: 2,
                    }}
                  />
                  
                  <Typography variant="h1" gutterBottom>
                    Beyond Treatment.
                  </Typography>
                  <Typography variant="h1" gutterBottom sx={{ color: '#ffffff', WebkitTextFillColor: '#ffffff' }}>
                    Advancing Liver Health.
                  </Typography>
                  <Typography variant="h1" gutterBottom sx={{ color: '#ffffff', WebkitTextFillColor: '#ffffff' }}>
                    Transforming Lives.
                  </Typography>
                  
                  <Typography variant="h5" sx={{ 
                    mt: 3, 
                    mb: 4, 
                    color: '#b0b0b0',
                    maxWidth: 600,
                    fontWeight: 300,
                    lineHeight: 1.8,
                  }}>
                    Delivering specialized liver care, infectious disease management,
                    advanced diagnostics, and preventive healthcare for every Liberian.
                  </Typography>

                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <Button 
                      variant="contained" 
                      size="large"
                      onClick={() => navigate('/book-appointment')}
                      endIcon={<ArrowForwardIcon />}
                      sx={{ 
                        fontSize: '1.1rem',
                        px: 5,
                        py: 1.8,
                      }}
                    >
                      Book Appointment
                    </Button>
                    <Button 
                      variant="outlined" 
                      size="large"
                      onClick={() => navigate('/services')}
                      sx={{ 
                        fontSize: '1.1rem',
                        px: 5,
                        py: 1.8,
                        borderColor: 'rgba(255,255,255,0.2)',
                      }}
                    >
                      Explore Services
                    </Button>
                  </Stack>

                  {/* Stats */}
                  <Grid container spacing={3} sx={{ mt: 6 }}>
                    {stats.map((stat, index) => (
                      <Grid item xs={6} md={3} key={index}>
                        <Grow in={isVisible} timeout={1000 + index * 200}>
                          <Paper sx={{ 
                            p: 2, 
                            textAlign: 'center',
                            bgcolor: 'rgba(255,255,255,0.03)',
                            border: '1px solid rgba(255,255,255,0.05)',
                          }}>
                            <Box sx={{ color: '#00d4ff' }}>{stat.icon}</Box>
                            <Typography variant="h4" fontWeight={800} color="white">
                              {stat.value}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {stat.label}
                            </Typography>
                          </Paper>
                        </Grow>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </Fade>
            </Grid>

            <Grid item xs={12} lg={5}>
              <Box sx={{ 
                position: 'relative',
                display: { xs: 'none', lg: 'block' },
              }}>
                <Box sx={{
                  position: 'relative',
                  width: '100%',
                  height: 500,
                  borderRadius: '30px',
                  overflow: 'hidden',
                  background: 'linear-gradient(135deg, rgba(0, 212, 255, 0.1) 0%, rgba(0, 102, 255, 0.1) 100%)',
                  border: '1px solid rgba(255,255,255,0.05)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h1" sx={{ fontSize: 80, mb: 2 }}>
                      🏥
                    </Typography>
                    <Typography variant="h4" color="white" fontWeight={700}>
                      HBMG
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      Excellence in Healthcare
                    </Typography>
                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center', gap: 2 }}>
                      <Chip label="24/7 Emergency" size="medium" sx={{ borderColor: '#00d4ff' }} />
                      <Chip label="Digital Health" size="medium" sx={{ borderColor: '#00ff88' }} />
                    </Box>
                  </Box>
                </Box>

                {/* Floating Badge */}
                <Paper sx={{
                  position: 'absolute',
                  top: -20,
                  right: -20,
                  p: 2,
                  bgcolor: 'rgba(0, 212, 255, 0.1)',
                  border: '1px solid rgba(0, 212, 255, 0.2)',
                  backdropFilter: 'blur(20px)',
                  animation: 'bounce 3s infinite',
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip label="⭐ 4.9" color="success" size="small" />
                    <Typography variant="body2" color="white">97% Satisfaction</Typography>
                  </Box>
                </Paper>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Services Section */}
      <Box sx={{ 
        py: 10,
        background: 'linear-gradient(180deg, #141414 0%, #0a0a0a 100%)',
        position: 'relative',
      }}>
        <Container maxWidth="xl">
          <Fade in={isVisible} timeout={1500}>
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <Chip label="Our Services" sx={{ mb: 2, borderColor: '#00d4ff' }} />
              <Typography variant="h2" gutterBottom>
                Centers of Excellence
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
                World-class healthcare services delivered with precision and compassion.
              </Typography>
            </Box>
          </Fade>

          <Grid container spacing={4}>
            {services.map((service, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Grow in={isVisible} timeout={1000 + index * 150}>
                  <Card sx={{ 
                    p: 3,
                    height: '100%',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: 3,
                      background: service.gradient,
                    }
                  }}>
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'space-between',
                      mb: 2,
                    }}>
                      <Box sx={{ color: '#00d4ff' }}>{service.icon}</Box>
                      <Chip 
                        label={service.badge} 
                        size="small" 
                        sx={{ 
                          background: service.gradient,
                          color: 'white',
                          fontWeight: 600,
                        }} 
                      />
                    </Box>
                    <Typography variant="h5" fontWeight={700} gutterBottom>
                      {service.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {service.description}
                    </Typography>
                    <Button 
                      endIcon={<ChevronRightIcon />} 
                      sx={{ mt: 2 }}
                    >
                      Learn More
                    </Button>
                  </Card>
                </Grow>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Features Section */}
      <Box sx={{ 
        py: 10,
        background: 'linear-gradient(180deg, #0a0a0a 0%, #141414 100%)',
      }}>
        <Container maxWidth="xl">
          <Fade in={isVisible} timeout={1800}>
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <Chip label="Why Choose Us" sx={{ mb: 2, borderColor: '#ff6b6b' }} />
              <Typography variant="h2" gutterBottom>
                Why HBMG Hospital?
              </Typography>
            </Box>
          </Fade>

          <Grid container spacing={3}>
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Grow in={isVisible} timeout={1000 + index * 200}>
                  <Paper sx={{ 
                    p: 3, 
                    textAlign: 'center',
                    height: '100%',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      borderColor: 'rgba(0, 212, 255, 0.2)',
                    }
                  }}>
                    <Box sx={{ 
                      width: 70, 
                      height: 70, 
                      borderRadius: '50%', 
                      background: 'rgba(0, 212, 255, 0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: 2,
                      color: '#00d4ff',
                    }}>
                      {feature.icon}
                    </Box>
                    <Typography variant="h6" fontWeight={700} gutterBottom>
                      {feature.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {feature.desc}
                    </Typography>
                  </Paper>
                </Grow>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box sx={{
        background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a2e 100%)',
        py: 10,
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: '-50%',
          left: '-50%',
          width: '200%',
          height: '200%',
          background: 'radial-gradient(circle at 30% 50%, rgba(0, 212, 255, 0.05) 0%, transparent 60%)',
          animation: 'spin 20s linear infinite',
        }
      }}>
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
          <Fade in={isVisible} timeout={2000}>
            <Box>
              <Typography variant="h2" gutterBottom>
                Ready to Transform Your Health?
              </Typography>
              <Typography variant="h5" sx={{ mb: 4, color: '#b0b0b0', fontWeight: 300 }}>
                Join thousands of patients who trust HBMG for world-class healthcare.
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
                <Button 
                  variant="contained" 
                  size="large"
                  onClick={() => navigate('/book-appointment')}
                  endIcon={<ArrowForwardIcon />}
                  sx={{ fontSize: '1.1rem', px: 5, py: 1.8 }}
                >
                  Book Appointment
                </Button>
                <Button 
                  variant="outlined" 
                  size="large"
                  onClick={() => navigate('/contact')}
                  sx={{ fontSize: '1.1rem', px: 5, py: 1.8 }}
                >
                  Contact Us
                </Button>
              </Stack>
            </Box>
          </Fade>
        </Container>
      </Box>

      {/* CSS Animations */}
      <style>
        {`
          @keyframes float {
            0%, 100% { transform: translateY(0px) scale(1); }
            50% { transform: translateY(-20px) scale(1.1); }
          }
          @keyframes bounce {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-10px); }
          }
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </Box>
  );
};

export default Home;
